import { Box, Flex, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';

import svgIcons from '@/assets/svg/icons';
import svgFiles from '@/assets/svg/svgFiles';
import CustomButton from '@/components/atoms/CustomButton';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import roles from '@/constants/roles';
import useContentAuthorProfile from '@/hooks/content/useContentAuthorProfile';
import useContentFeed from '@/hooks/content/useContentFeed';
import ContentCardSkeletonGrid from '@/modules/content/common/ContentCardSkeletonGrid';
import ContentEmptyState from '@/modules/content/common/ContentEmptyState';
import ThumbnailPreview from '@/modules/content/common/ThumbnailPreview';
import { contentFocusOptions } from '@/modules/contentCreator/creatorSchema';
import { background, secondary, text } from '@/theme/colors/palettes';
import { useAppStore } from '@/store/useAppStore';

const getRoleLabel = (userRoles: string[] = []) => {
  if (userRoles.includes(roles.peerSupport)) return 'Peer Support';
  if (userRoles.includes(roles.orgAdmin)) return 'Organization Admin';
  return 'Content Creator';
};

const CreatorProfile = () => {
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const { organization } = useAppStore();

  const { profile } = useContentAuthorProfile(userId);
  const { content, isLoading, isError } = useContentFeed({
    authorUserId: userId,
  });

  const authorName = profile?.creatorDisplayName?.trim() || profile?.name || 'Creator';
  const creator = content.find((item) => item.creator?.id === userId)?.creator;
  const roleLabel = getRoleLabel(profile?.roles ?? creator?.roles);
  const organizationName = creator?.organizationName ?? organization?.name;
  const creatorLine = [roleLabel, organizationName].filter(Boolean).join(' | ');
  const contentFocus = (profile?.contentFocus ?? [])
    .map((value) => contentFocusOptions.find((option) => option.value === value)?.label ?? value)
    .join(', ');

  return (
    <VStack width={MAX_CONTAINER_WIDTH} marginTop={'20px'} gap={'20px'} alignItems={'center'}>
      <HStack>
        <CustomButton
          isTransparent
          iconBeforeLabelSvgPath={svgIcons.leftArrow}
          iconBeforeLabelSvgBoxHeight={12}
          iconBeforeLabelSvgColor={text.mediumBlue}
          labelColor={'text.mediumBlue'}
          labelHoverColor={'extra.black'}
          label={'Back to Explore'}
          onClick={() => navigate({ to: '/explore' })}
        />
      </HStack>

      {/* Creator identity sits above an overlapping profile panel, matching the public profile design. */}
      <VStack align="center" gap="5px" textAlign="center" paddingTop={{ base: '6px', md: '12px' }}>
        <Text
          fontFamily="Lora"
          fontStyle="italic"
          fontSize={'34px'}
          fontWeight={500}
          lineHeight="1.15"
          color={text.mediumBlue}
        >
          {authorName}
        </Text>
        <Text fontSize={'16px'} color={text.mediumBlue} fontWeight={500}>
          {creatorLine}
        </Text>
        <HStack gap={'20px'} flexWrap="wrap" justify="center" paddingTop="3px">
          <HStack gap="6px">
            <Image src={svgFiles.play} alt="" width="18px" height="18px" />
            <Text color={secondary[500]} fontWeight={800} fontSize="12px" textTransform="uppercase">
              {profile?.contentCreated ?? content.length} Content
            </Text>
          </HStack>
        </HStack>
      </VStack>

      <Box
        position="relative"
        marginTop={'70px'}
        backgroundColor={background.lightGray}
        borderRadius={{ base: '18px', md: '24px' }}
        boxShadow="0px 10px 80px rgba(0, 65, 121, 0.12)"
        padding={'50px 70px'}
        width={'820px'}
      >
        <Box
          position="absolute"
          top={'-65px'}
          left="50%"
          transform="translateX(-50%)"
          width={{ base: '104px', md: '130px' }}
          height={{ base: '104px', md: '130px' }}
          borderRadius="full"
          overflow="hidden"
          border="4px solid white"
          backgroundColor={background.lightPurple}
          boxShadow="0px 12px 24px 0px #0E006224"
        >
          <Image
            src={profile?.photoUrl || svgFiles.genericUser}
            alt={authorName}
            width="100%"
            height="100%"
            objectFit="cover"
          />
        </Box>

        <VStack align="stretch" gap={'28px'} marginTop={'40px'}>
          {profile?.headline && (
            <VStack align="stretch" gap="8px">
              <Text color={text.mediumBlue} fontSize="18px" fontWeight={800}>
                About Me
              </Text>
              <Text
                color={text.mediumBlue}
                fontSize={{ base: '14px', md: '18px' }}
                fontWeight={500}
                lineHeight="1.35"
                whiteSpace="pre-wrap"
              >
                {profile.headline}
              </Text>
            </VStack>
          )}

          {contentFocus && (
            <VStack align="stretch" gap="2px">
              <Text color={text.mediumBlue} fontSize="16px" fontWeight={800}>
                Content Focus
              </Text>
              <Text color={text.mediumBlue} fontSize={{ base: '14px', md: '17px' }} lineHeight="1.35">
                {contentFocus}
              </Text>
            </VStack>
          )}
        </VStack>
      </Box>

      <Text variant={'loraMediumTitle'} color={text.mediumBlue} mt={'40px'}>
        Published content
      </Text>

      {isLoading ? (
        <ContentCardSkeletonGrid count={6} />
      ) : isError ? (
        <ContentEmptyState
          icon={TriangleAlert}
          title="Couldn’t load this creator’s content"
          description="Something went wrong. Please try again."
        />
      ) : content.length === 0 ? (
        <Text color={'text.mediumBlue'} paddingY={'30px'}>
          This creator hasn’t published any content yet.
        </Text>
      ) : (
        <Flex width={'100%'} gap={'16px'} wrap={'wrap'} justify={'flex-start'}>
          {content.map((item) => (
            <Box
              key={item.id}
              role={'group'}
              cursor={'pointer'}
              borderRadius={'14px'}
              transition={'transform 0.25s ease, box-shadow 0.25s ease'}
              _hover={{
                transform: 'translateY(-6px)',
                boxShadow: '0px 16px 32px 0px rgba(14, 0, 98, 0.22)',
              }}
              onClick={() =>
                navigate({
                  to: '/loop/$contentId',
                  params: { contentId: item.id },
                })
              }
            >
              <ThumbnailPreview
                type={item.type}
                title={item.title}
                description={item.description}
                authorLabel={authorName}
                domains={item.domains}
                existingUrl={item.effectiveThumbnailUrl ?? item.thumbnailUrl}
                file={null}
                onFileChange={() => {}}
                isPublic={item.visibility?.public ?? false}
                readOnly
              />
            </Box>
          ))}
        </Flex>
      )}
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_explore/explore/creator/$userId')({
  staticData: { title: 'Creator' },
  component: CreatorProfile,
});
